import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
import FocusTrap from "focus-trap-react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { defaultDateFormat } from "../date-input/dateFormats";
import { isValidDate } from "../date-input/utils";
import { Popper } from "../../popper/Popper";
import { PopperBox } from "../../popper/PopperBox";
import { CalendarFooter } from "../date-calendar/CalendarFooter";
import { DateCalendar } from "../date-calendar/DateCalendar";
import { useDateField } from "./useDateField";
import { stringMapDefault } from "./DatePicker.stringMap";
import * as Styled from "./DatePicker.styled";
const componentName = "DatePicker";
export const DatePicker = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { calendarProps = {}, dateFormat = defaultDateFormat, date, label, validate, placeholder, onDateChange = () => { }, onFocusOutside, disabled, inputProps, inputRef: inputRefProp, calendarDisplay = "popover", layoutVariant, popperProps, shouldCloseOnSelect = true, shouldValidateOnBlur = true, status, stringMap: stringMapProp, locale, ...fieldProps } = props;
    // @ts-expect-error -- TODO: update API specifically for components with subcomponents
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const { toggleCalendar, closeCalendar, calendarDescription, invalidDate, ...labels } = stringMap;
    const normalizedDateFormat = dateFormat.replace("mm", "MM");
    const closeTimeoutRef = useRef();
    const calendarRef = useRef(null);
    const [hasFocus, setHasFocus] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    // This is to prevent the popover from blinking on close
    const closePopover = () => {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = window.setTimeout(() => {
            setIsOpen(false);
        }, 0);
        inputRef.current?.focus();
    };
    const { dateField, calendarButtonRef, inputRef, setValidationMessage } = useDateField({
        ref,
        dateFormat: normalizedDateFormat,
        status,
        calendarRef,
        hasFocus,
        hasPopover: calendarDisplay === "popover",
        inputRef: inputRefProp,
        date,
        layoutVariant,
        label,
        validate,
        placeholder,
        inputProps,
        disabled,
        onDateChange,
        onKeyDown: (e) => {
            if (e.key === " ") {
                setIsOpen(true);
                e.preventDefault();
            }
        },
        stringMap: { toggleCalendar, invalidDate },
        shouldValidateOnBlur,
        ...fieldProps,
        onFocus: () => setHasFocus(true),
        onFocusOutside: (e) => {
            setHasFocus(false);
            onFocusOutside?.(e);
        }
    });
    const { onDayClick, mode, ...dayPickerProps } = calendarProps;
    useEffect(() => () => {
        window.clearTimeout(closeTimeoutRef.current);
    }, []);
    // Close on delay to allow selection animation to play before
    // closing the popover
    const closeOnDelay = () => {
        if (!shouldCloseOnSelect || calendarDisplay !== "popover") {
            return;
        }
        const close = () => setIsOpen(false);
        if (process.env.NODE_ENV === "test") {
            close();
            return;
        }
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = window.setTimeout(close, 250);
    };
    const calendar = calendarDisplay === "none" ? null : (_jsx(DateCalendar, { initialFocus: calendarDisplay === "popover", isInline: calendarDisplay === "inline", selected: date && isValidDate(date) ? date : undefined, defaultMonth: date && isValidDate(date) ? date : undefined, locale: locale, labels: labels, onDayClick: (day, activeModifiers, e) => {
            if (validate !== false) {
                setValidationMessage("");
            }
            onDateChange(day);
            onDayClick?.(day, activeModifiers, e);
            closeOnDelay();
        }, ...dayPickerProps }));
    return (_jsxs(_Fragment, { children: [dateField, calendarDisplay === "popover" ? (_jsx(Popper, { placement: "bottom-end", fallbackPlacements: ["top-end", "bottom-start", "top-start"], popoverOptions: { mode: "manual", returnFocusRef: inputRef }, placementRef: inputRef, triggerRef: calendarButtonRef, isOpen: isOpen, onOpen: () => setHasFocus(true), onOpenCallback: () => {
                    // onOpen callback
                    setIsOpen(true);
                }, onClose: closePopover, onCloseCallback: () => {
                    setIsOpen(false);
                }, overlay: ({ motionState, closeAndReturnFocus }) => (_jsx(FocusTrap, { active: motionState === "opened", focusTrapOptions: {
                        clickOutsideDeactivates: true,
                        returnFocusOnDeactivate: false,
                        // https://github.com/focus-trap/focus-trap-react#testing-in-jsdom
                        tabbableOptions: { displayCheck: "none" }
                    }, children: _jsx(Styled.DatePickerPopperWrapper, { "data-testid": "DatePickerPopperBoxWrapper", children: _jsxs(PopperBox, { ref: calendarRef, maxWidth: "none", hasPadding: false, role: "dialog", "aria-label": calendarDescription, children: [calendar, shouldCloseOnSelect ? null : (_jsx(CalendarFooter, { stringMap: { closeCalendar }, onClose: closeAndReturnFocus }))] }) }) })) })) : (calendar)] }));
});
DatePicker.displayName = componentName;
